'use client';

import { Popover } from '@common/components';
import { cn } from '@core/utils';
import type { Placement } from '@floating-ui/react';
import { useEffect, useRef } from 'react';

const useFocusTrap = (open: boolean, onClose: () => void) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  // eslint-disable-next-line react-hooks/refs -- 최신 onClose 콜백을 effect 재구독 없이 참조하는 의도적 latest-ref 패턴
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;

    const element = contentRef.current;
    if (!element) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCloseRef.current();

        return;
      }

      if (e.key !== 'Tab') return;

      const focusable = element.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  return contentRef;
};

interface HeaderActionPopoverProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trigger: React.ReactNode;
  children: React.ReactNode;
  contentClassName?: string;
  maxFloatingHeight?: number;
  triggerClassName?: string;
  placement?: Placement;
  floatingPadding?: number;
}

export const HeaderActionPopover = ({
  open,
  onOpenChange,
  trigger,
  children,
  contentClassName,
  maxFloatingHeight,
  triggerClassName,
  placement = 'bottom-end',
  floatingPadding = 8,
}: HeaderActionPopoverProps) => {
  const contentRef = useFocusTrap(open, () => onOpenChange(false));

  return (
    <Popover.Root
      placement={placement}
      floatingPadding={floatingPadding}
      maxFloatingHeight={maxFloatingHeight}
      open={open}
      onOpenChange={onOpenChange}
      preventCloseOnContentClick
    >
      <Popover.Trigger asChild className={triggerClassName}>
        {trigger}
      </Popover.Trigger>
      <Popover.Content
        className={cn('z-30 rounded-xxlarge p-0 shadow-lg', contentClassName)}
      >
        <div ref={contentRef}>{children}</div>
      </Popover.Content>
    </Popover.Root>
  );
};
